import {
	Button,
	ConfirmationDialog,
	Loading,
	SelectFilter,
	Table,
	Title,
} from 'src/components';
import { type ChangeEvent, useEffect, useState } from 'react';
import { Grid, MenuItem } from '@material-ui/core';
import { type IInstitution, type IUserType } from 'src/models';
import { ListAlt as LayoutIcon } from '@material-ui/icons';
import { userTypes } from 'src/helpers';
import useStyles from './Users-styles';

import LayouUploadSummaryDialog from './LayoutUploadSummaryDialog';
import LayoutUploadDialog from './LayoutUploadDialog';
import UserCoursesDialog from './UserCoursesDialog';
import UserDrawerForm from './UserDrawerForm';

import { type RootState } from 'src/store';
import { useDispatch, useSelector } from 'react-redux';
import institutionActions from 'src/store/Institutions/actions';
import userActions from 'src/store/Users/actions';

function Users() {
	const classes = useStyles();
	// NOTE: Ver todas las notas de ./src/modules/private/Admin.tsx
	const dispatch = useDispatch();

	const getUsers = (type: IUserType, page?: number, pageSize?: number) =>
		dispatch(userActions.getAll(type, page, pageSize));
	const getUsersByInstitution = (
		institutionId: number,
		type: IUserType,
		page?: number,
		pageSize?: number,
	) =>
		dispatch(
			userActions.getByInstitution(institutionId, type, page, pageSize),
		);
	const getInstitutions = () => dispatch(institutionActions.getAll());
	const remove = (id: string) => dispatch(userActions.remove(id));
	const set = (type: string, value: any) =>
		dispatch(userActions.set(type, value));
	const clearUsers = () => dispatch(userActions.setAll([]));

	const auth = useSelector((state: RootState) => state.auth.data);
	const { institutionId, isLoading, metadata, page, pageSize, type, users } =
		useSelector((state: RootState) => state.users);
	const { institutions } = useSelector(
		(state: RootState) => state.institutions,
	);
	const [drawer, setDrawer] = useState({ open: false, userId: '' });
	const [confirmation, setConfirmation] = useState({
		open: false,
		userId: '',
	});
	const [layoutOpen, setLayoutOpen] = useState(false);
	const [userCoursesOpen, setUserCourseOpen] = useState(false);

	useEffect(() => {
		if (auth.type === userTypes.ADMIN) {
			if (institutionId != 0)
				getUsersByInstitution(institutionId, type, 1, pageSize);
			else getUsers(type, 1, pageSize);
		} else {
			getUsersByInstitution(
				auth.institutionId,
				userTypes.USER,
				1,
				pageSize,
			);
		}
	}, []);

	function handlePagination(event: any, newPage: number) {
		if (auth.institutionId || institutionId)
			getUsersByInstitution(
				auth.institutionId || institutionId,
				userTypes.USER,
				newPage,
				pageSize,
			);
		else getUsers(type, newPage, pageSize);
	}

	function handleTypeFilter(event: ChangeEvent<HTMLInputElement>) {
		const type = event.target.value;
		set('type', type);

		if (type === userTypes.INSTITUTION || type === userTypes.USER) {
			clearUsers();
			getInstitutions();

			if (institutionId != 0) {
				getUsersByInstitution(institutionId, type, 1, pageSize);
			}
		} else {
			getUsers(type, 1, pageSize);
		}
	}

	function handleByInstitution(event: ChangeEvent<HTMLInputElement>) {
		getUsersByInstitution(parseInt(event.target.value), type, 1, pageSize);
	}

	const columns = [
		{
			key: 'username',
			label: 'Usuario',
			render: (value: any, row: any) => value,
		},
		{
			key: 'email',
			label: 'Email',
			render: (value: any, row: any) => value,
		},
		{
			key: 'firstname',
			label: 'Nombre(s)',
			render: (value: any, row: any) => value,
		},
		{
			key: 'lastname',
			label: 'Apellidos',
			render: (value: any, row: any) => value,
		},
		{
			key: 'department',
			label: 'Departamento',
			render: (value: any, row: any) => value,
		},
	];

	const actions = [
		{
			label: 'Editar',
			onClick: (row: any) => {
				setDrawer({ open: true, userId: row.id });
			},
		},
		{
			label: 'Eliminar',
			onClick: (row: any) => {
				setConfirmation({ open: true, userId: row.id });
			},
		},
	];

	return (
		<Grid container>
			<UserDrawerForm
				onClose={() => {
					setDrawer({ open: false, userId: '' });
				}}
				open={drawer.open}
				userId={drawer.userId}
			/>

			<ConfirmationDialog
				onClose={() => {
					setConfirmation({ open: false, userId: '' });
				}}
				onConfirm={() => remove(confirmation.userId)}
				open={confirmation.open}
				title={'¿Deseas eliminar este usuario?'}
			/>

			<LayoutUploadDialog
				handleOpen={() => {
					setLayoutOpen(false);
				}}
				open={layoutOpen}
			/>

			<UserCoursesDialog
				handleOpen={() => {
					setUserCourseOpen(false);
				}}
				institutionId={institutionId}
				open={userCoursesOpen}
			/>

			<LayouUploadSummaryDialog />

			<Grid item xs={6}>
				<Title
					breadcrumbs={[{ label: 'Usuarios' }]}
					title={'Usuarios'}
				/>
			</Grid>

			<Grid className={classes.buttonContainer} item xs={6}>
				<Button
					hideLabelOnMobile
					onClick={() => {
						setLayoutOpen(true);
					}}
					startIcon={<LayoutIcon />}
					style={{ marginRight: 8 }}
				>
					Carga layout
				</Button>

				<Button
					actionType={'add'}
					hideLabelOnMobile
					onClick={() => {
						setDrawer({ open: true, userId: '' });
					}}
				>
					Agregar
				</Button>
			</Grid>

			<Grid className={classes.filters} container item xs={12}>
				{auth.type === userTypes.ADMIN && (
					<Grid item lg={6} xs={12}>
						<SelectFilter
							handleChange={handleTypeFilter}
							label={'Tipo'}
							value={type}
						>
							<MenuItem value={userTypes.ADMIN}>
								{' '}
								Administrador{' '}
							</MenuItem>

							<MenuItem value={userTypes.INSTITUTION}>
								{' '}
								Institución{' '}
							</MenuItem>

							<MenuItem value={userTypes.USER}>
								{' '}
								Usuario{' '}
							</MenuItem>
						</SelectFilter>

						{(type === userTypes.INSTITUTION ||
							type === userTypes.USER) && (
								<div style={{ paddingTop: 8 }}>
									<SelectFilter
										handleChange={handleByInstitution}
										label={'Institución'}
										value={institutionId}
									>
										<MenuItem value={0}>
											{' '}
											Selecciona una institución{' '}
										</MenuItem>

										{institutions.map(
											(institution: IInstitution) => (
												<MenuItem
													key={institution.id}
													value={institution.id}
												>
													{institution.name}
												</MenuItem>
											),
										)}
									</SelectFilter>
								</div>
							)}
					</Grid>
				)}

				{type === userTypes.USER ? (
					<Grid className={classes.assign} item lg={6} xs={12}>
						<Button
							color={'primary'}
							disabled={!institutionId}
							onClick={() => {
								setUserCourseOpen(true);
							}}
							variant={'outlined'}
						>
							Asignar cursos
						</Button>
					</Grid>
				) : (
					<div />
				)}
			</Grid>

			<Loading isLoading={isLoading}>
				<Table
					actions={actions}
					columns={columns}
					data={users}
					handlePagination={handlePagination}
					metadata={metadata}
					page={page}
				/>
			</Loading>
		</Grid>
	);
}

export default Users;
